import type { AIModel, PerPromptStat } from '@ekorank/shared'
import { AI_MODELS } from '@ekorank/shared'
import { modelLabel } from '@/lib/modelDisplay'
import type { ResultFilters } from '@/store/dashboard'
import { ModelIcon } from '@/components/ModelIcon'

interface Props {
  filters: ResultFilters
  perPrompt: PerPromptStat[]
  onChange: (partial: Partial<ResultFilters>) => void
  onReset: () => void
}

export function FilterBar({ filters, perPrompt, onChange, onReset }: Props) {
  const toggleModel = (model: AIModel) => {
    const next = filters.models.includes(model)
      ? filters.models.filter((m) => m !== model)
      : [...filters.models, model]
    onChange({ models: next })
  }

  const fromDate = filters.dateFrom ? toInputDate(filters.dateFrom) : ''
  const toDate = filters.dateTo ? toInputDate(filters.dateTo) : ''

  return (
    <div className="border-b border-outline-variant/10 p-3 sm:p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono text-outline uppercase tracking-widest mr-1">Models:</span>
        {AI_MODELS.map((m) => {
          const selected = filters.models.includes(m)
          return (
            <button
              key={m}
              onClick={() => toggleModel(m)}
              className={`px-2 py-1 rounded text-xs font-mono border transition-colors inline-flex items-center gap-1.5 ${
                selected
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-outline-variant/20 text-outline hover:border-outline-variant/40'
              }`}
            >
              <ModelIcon model={m} size={12} />
              {modelLabel(m)}
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-2 text-outline">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(e) => onChange({ dateFrom: e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null })}
            className="bg-surface-container-high border border-outline-variant/20 rounded px-2 py-1 text-on-surface-variant"
          />
        </label>
        <label className="flex items-center gap-2 text-outline">
          To
          <input
            type="date"
            value={toDate}
            onChange={(e) => onChange({ dateTo: e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null })}
            className="bg-surface-container-high border border-outline-variant/20 rounded px-2 py-1 text-on-surface-variant"
          />
        </label>
        <label className="flex items-center gap-2 text-outline cursor-pointer">
          <input
            type="checkbox"
            checked={filters.mentionedOnly}
            onChange={(e) => onChange({ mentionedOnly: e.target.checked })}
            className="accent-secondary"
          />
          Mentioned only
        </label>
        {perPrompt.length > 0 && (
          <label className="flex items-center gap-2 text-outline">
            Prompt
            <select
              value={filters.promptId ?? ''}
              onChange={(e) => onChange({ promptId: e.target.value || null })}
              className="bg-surface-container-high border border-outline-variant/20 rounded px-2 py-1 text-on-surface-variant max-w-[220px] truncate"
            >
              <option value="">All</option>
              {perPrompt.map((p) => (
                <option key={p.promptId} value={p.promptId}>{p.promptText}</option>
              ))}
            </select>
          </label>
        )}
        <button
          onClick={onReset}
          className="ml-auto px-2 py-1 rounded text-outline hover:text-white text-xs"
        >
          Reset filters
        </button>
      </div>
    </div>
  )
}

function toInputDate(unixSec: number): string {
  const d = new Date(unixSec * 1000)
  return d.toISOString().slice(0, 10)
}
